import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  Tooltip,
  Avatar,
  Link,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

interface PersonHeaderProps {
  name: string;
  types: string[];
  email?: string | null;
  phone?: string | null;
  avatarUrl?: string | null;
  directorshipsCount: number;
  shareholdingsCount: number;
  onEdit: () => void;
  onMerge: () => void;
  hasPermission: (permission: string) => boolean;
  getInitials: (name: string) => string;
}

const PersonHeader: React.FC<PersonHeaderProps> = ({
  name,
  types,
  email,
  phone,
  avatarUrl,
  directorshipsCount,
  shareholdingsCount,
  onEdit,
  onMerge,
  hasPermission,
  getInitials,
}) => (
  <Paper sx={{ p: 3, mb: 3 }}>
    <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Avatar
          src={avatarUrl ?? undefined}
          sx={{
            width: 80,
            height: 80,
            bgcolor: 'primary.main',
            fontSize: '2rem',
          }}
        >
          {!avatarUrl && getInitials(name || 'P')}
        </Avatar>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 0.5 }}>
            {name}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
            {types.length > 0 ? (
              types.map((type: string) => (
                <Chip
                  key={type}
                  label={type}
                  size="small"
                  color={type === 'DIRECTOR' ? 'primary' : type === 'SHAREHOLDER' ? 'success' : 'default'}
                  icon={type === 'DIRECTOR' ? <WorkIcon /> : <AccountBalanceIcon />}
                />
              ))
            ) : (
              <Chip label="Person" size="small" icon={<PersonIcon />} />
            )}
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap">
            {email && (
              <Link
                href={`mailto:${email}`}
                underline="hover"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}
              >
                <EmailIcon fontSize="small" />
                {email}
              </Link>
            )}
            {phone && (
              <Link
                href={`tel:${phone}`}
                underline="hover"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}
              >
                <PhoneIcon fontSize="small" />
                {phone}
              </Link>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Director at {directorshipsCount} companies · Shareholder in {shareholdingsCount} companies
          </Typography>
        </Box>
      </Box>
      <Stack direction="row" spacing={1}>
        {hasPermission('persons:update') && (
          <Tooltip title="Edit person details">
            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={onEdit}>
              Edit
            </Button>
          </Tooltip>
        )}
        {hasPermission('persons:manage') && (
          <Tooltip title="Combine with another person">
            <Button size="small" variant="outlined" startIcon={<MergeTypeIcon />} onClick={onMerge}>
              Combine
            </Button>
          </Tooltip>
        )}
      </Stack>
    </Box>
  </Paper>
);

export default PersonHeader;
